/**
 * ClientViewHandler.ts
 * BATON handler for UI view/navigation synchronization
 * 
 * Follows the same pattern as ClientTransportHandler.ts
 * Synchronizes UI navigation and selection state across all connected clients
 * via the BATON protocol. Server relays commands as events to all OTHER clients.
 * 
 * Commands: navigate, select, deselect
 * Events: viewChanged, selectionChanged
 * 
 * Location: cloud (server is the relay)
 */

import {
  BatonMessage,
  CommandPayload,
  EventPayload,
  ResponsePayload,
  HandlerId,
  HandlerLocation,
  HandlerCapabilities,
  createCommand,
  validateMessage,
  PROTOCOL_VERSION,
} from '../protocols/BATON';
import { useMaestroStore } from '../store/maestroStore';

// =============================================================================
// TYPES
// =============================================================================

/** View event payload from server */
export interface ViewEvent {
  event: 'viewChanged' | 'selectionChanged' | 'zoneChanged';
  screen?: string;
  params?: Record<string, unknown>;
  itemType?: 'track' | 'album' | 'artist';
  index?: number | null;
  itemId?: string;
  context?: string;
  zoneName?: string;
  sourceDeviceId: string;
  /** Per-client-instance identifier that originated the action (tab/app instance). */
  originClientInstanceId?: string;
}

/** Callback for view events */
export type ViewEventCallback = (event: ViewEvent) => void;

/** Callback for view changed events */
export type ViewChangedCallback = (screen: string, params?: Record<string, unknown>) => void;

/** Callback for selection changed events */
export type SelectionChangedCallback = (itemType: string, index: number | null, context?: string) => void;

/** Callback for zone changed events */
export type ZoneChangedCallback = (zoneName: string) => void;

// =============================================================================
// DEVICE ID HELPER
// =============================================================================

/**
 * Get or generate a persistent device ID
 * Reuses the same ID storage as ClientTransportHandler for consistency
 */
function getDeviceId(): string {
  if (typeof localStorage !== 'undefined') {
    let id = localStorage.getItem('maestro_device_id');
    if (!id) {
      const platform = /iPhone|iPad|iPod/.test(navigator.userAgent) ? 'ios' : 'web';
      id = `${platform}_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
      localStorage.setItem('maestro_device_id', id);
    }
    return id;
  }
  return `temp_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Session-scoped client instance id (tab/app instance).
 * This is distinct from deviceId: multiple tabs on one device should not share identity.
 */
function getClientInstanceId(): string {
  try {
    const key = 'maestro_client_instance_id';
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const id = `ci_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 10)}`;
    window.sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `ci_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 10)}`;
  }
}

// =============================================================================
// CLIENT VIEW HANDLER
// =============================================================================

export class ClientViewHandler {
  readonly handlerId: HandlerId = 'view';
  readonly location: HandlerLocation = 'cloud';
  
  private ws: WebSocket | null = null;
  private eventListeners: Set<ViewEventCallback> = new Set();
  private deviceId: string;
  private clientInstanceId: string;
  private lastOfflineLogAt = 0;
  
  constructor(deviceId?: string) {
    this.deviceId = deviceId || getDeviceId();
    this.clientInstanceId = getClientInstanceId();
    console.log(`[ClientViewHandler] Initialized with deviceId: ${this.deviceId}`);
  }
  
  // ---------------------------------------------------------------------------
  // CONNECTION
  // ---------------------------------------------------------------------------
  
  /**
   * Connect to WebSocket (called during app init)
   * Reuses the same WebSocket connection as ClientTransportHandler
   */
  connect(ws: WebSocket): void {
    this.ws = ws;
    console.log('[ClientViewHandler] 🔌 WebSocket connected');
  }
  
  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.ws = null;
    console.log('[ClientViewHandler] 🔌 WebSocket disconnected');
  }
  
  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
  
  // ---------------------------------------------------------------------------
  // SEMANTIC METHODS (UI calls these)
  // ---------------------------------------------------------------------------
  
  /**
   * Navigate to a new screen
   * 
   * @param screen - Screen identifier (e.g., 'album_detail', 'artist', 'search')
   * @param params - Optional parameters for the screen (e.g., { albumId: '123' })
   * 
   * @example
   * viewHandler.navigate('album_detail', { albumId: '1', albumTitle: 'Dark Side of the Moon' })
   */
  navigate(screen: string, params?: Record<string, unknown>): void {
    console.log(`[ClientViewHandler] 📍 navigate: ${screen}`, params);
    
    const message = createCommand(this.handlerId, {
      cmd: 'navigate',
      screen,
      params,
      originClientInstanceId: this.clientInstanceId,
    } as CommandPayload);
    
    this.send(message);
  }
  
  /**
   * Select an item (track, album, or artist)
   * 
   * @param itemType - Type of item being selected
   * @param index - Optional index in list
   * @param itemId - Optional item ID
   * @param context - Optional context (e.g., 'album_1' for track selection within an album)
   * 
   * @example
   * viewHandler.select('track', 2, undefined, 'album_1')
   * viewHandler.select('album', undefined, 'album_123')
   */
  select(
    itemType: 'track' | 'album' | 'artist',
    index?: number,
    itemId?: string,
    context?: string
  ): void {
    console.log(`[ClientViewHandler] 👆 select: ${itemType}`, { index, itemId, context });
    
    const message = createCommand(this.handlerId, {
      cmd: 'select',
      itemType,
      index,
      itemId,
      context,
      originClientInstanceId: this.clientInstanceId,
    } as CommandPayload);
    
    this.send(message);
  }
  
  /**
   * Clear selection
   * 
   * @param itemType - Type of item to deselect
   * @param context - Optional context for scoped deselection
   * 
   * @example
   * viewHandler.deselect('track', 'album_1')
   */
  deselect(itemType: 'track' | 'album' | 'artist', context?: string): void {
    console.log(`[ClientViewHandler] 👇 deselect: ${itemType}`, { context });
    
    const message = createCommand(this.handlerId, {
      cmd: 'deselect',
      itemType,
      context,
      originClientInstanceId: this.clientInstanceId,
    } as CommandPayload);
    
    this.send(message);
  }
  
  /**
   * Set the playback zone (output device)
   * Syncs across all connected clients
   * 
   * @param zoneName - Name of the zone (e.g., 'iPhone', 'Living Room', 'Kitchen')
   * 
   * @example
   * viewHandler.setZone('Living Room')
   */
  setZone(zoneName: string): void {
    console.log(`[ClientViewHandler] 🔊 setZone: ${zoneName}`);
    
    const message = createCommand(this.handlerId, {
      cmd: 'setZone',
      zoneName,
    } as CommandPayload);
    
    this.send(message);
  }
  
  // ---------------------------------------------------------------------------
  // EVENT HANDLING
  // ---------------------------------------------------------------------------
  
  /**
   * Called when WebSocket receives a view event from server
   * Routes to handleEvent if it's for this handler
   * 
   * @param message - Raw BATON message from WebSocket
   */
  handleMessage(message: BatonMessage | string): void {
    // Parse if string
    const msg: BatonMessage = typeof message === 'string' ? JSON.parse(message) : message;
    
    // Validate
    if (!validateMessage(msg)) {
      console.warn('[ClientViewHandler] Invalid message format:', msg);
      return;
    }
    
    // Only handle events for this handler
    if (msg.handler !== this.handlerId || msg.type !== 'event') {
      return;
    }
    
    // Extract and handle the event
    const payload = msg.payload as ViewEvent;
    this.handleEvent(payload);
  }
  
  /**
   * Handle a view event from the server
   * Called when another client navigates or selects something
   * 
   * @param event - The view event payload
   */
  handleEvent(event: ViewEvent): void {
    // Don't process same-origin events (prevents echo loops during reconnect / multi-device sync).
    if (event.originClientInstanceId && event.originClientInstanceId === this.clientInstanceId) {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log('[ClientViewHandler] Ignoring same-origin event');
      return;
    }
    
    console.log(`[ClientViewHandler] 📥 Event received: ${event.event} from ${event.sourceDeviceId}`);

    if (event.event === 'zoneChanged') {
      const { rendererHasStream } = useMaestroStore.getState();
      if (rendererHasStream) {
        console.log('[zoneChanged] suppressed — stream active');
        return;
      }
    }
    
    // Notify all listeners
    this.eventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (err) {
        console.error('[ClientViewHandler] Event listener error:', err);
      }
    });
  }
  
  /**
   * Subscribe to all view events
   * 
   * @param callback - Called when any view event is received
   * @returns Unsubscribe function
   */
  onEvent(callback: ViewEventCallback): () => void {
    this.eventListeners.add(callback);
    try {
      const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
      if (dbg) console.log(`[ClientViewHandler] Event subscriber added (total: ${this.eventListeners.size})`);
    } catch {}
    
    return () => {
      this.eventListeners.delete(callback);
      try {
        const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
        if (dbg) console.log(`[ClientViewHandler] Event subscriber removed (total: ${this.eventListeners.size})`);
      } catch {}
    };
  }
  
  /**
   * Subscribe to view changed events (another client navigated)
   * 
   * @param callback - Called with (screen, params) when another client navigates
   * @returns Unsubscribe function
   * 
   * @example
   * viewHandler.onViewChanged((screen, params) => {
   *   console.log(`Another device navigated to ${screen}`);
   *   // Optionally follow them: setCurrentScreen(screen);
   * });
   */
  onViewChanged(callback: ViewChangedCallback): () => void {
    return this.onEvent((event) => {
      if (event.event === 'viewChanged' && event.screen) {
        callback(event.screen, event.params);
      }
    });
  }
  
  /**
   * Subscribe to selection changed events (another client selected/deselected something)
   * 
   * @param callback - Called with (itemType, index, context) when selection changes
   * @returns Unsubscribe function
   * 
   * @example
   * viewHandler.onSelectionChanged((itemType, index, context) => {
   *   if (itemType === 'track' && index !== null) {
   *     setSelectedTrackIndex(index);
   *   }
   * });
   */
  onSelectionChanged(callback: SelectionChangedCallback): () => void {
    return this.onEvent((event) => {
      if (event.event === 'selectionChanged' && event.itemType) {
        callback(event.itemType, event.index ?? null, event.context);
      }
    });
  }
  
  /**
   * Subscribe to zone changed events (another client changed the playback zone)
   * 
   * @param callback - Called with (zoneName) when zone changes
   * @returns Unsubscribe function
   * 
   * @example
   * viewHandler.onZoneChanged((zoneName) => {
   *   setStoreZoneName(zoneName);
   * });
   */
  onZoneChanged(callback: ZoneChangedCallback): () => void {
    return this.onEvent((event) => {
      if (event.event === 'zoneChanged' && event.zoneName) {
        callback(event.zoneName);
      }
    });
  }
  
  // ---------------------------------------------------------------------------
  // INTERNAL
  // ---------------------------------------------------------------------------
  
  /**
   * Send a BATON message via WebSocket
   */
  private send(message: BatonMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      console.log(`[ClientViewHandler] 📤 Sent: ${(message.payload as any)?.cmd}`);
    } else {
      // View sync is best-effort; navigation must work even if server sync is down.
      // Avoid spamming the console on every click; only log when explicit debug is enabled.
      try {
        const dbg = window?.localStorage?.getItem('maestro_debug') === '1';
        if (!dbg) return;
        const now = Date.now();
        if (now - this.lastOfflineLogAt > 5000) {
          this.lastOfflineLogAt = now;
          console.warn('[ClientViewHandler] ⚠️ View sync skipped (WebSocket not connected)');
        }
      } catch {
        // ignore
      }
    }
  }
  
  // ---------------------------------------------------------------------------
  // BATON HANDLER INTERFACE (Required methods)
  // ---------------------------------------------------------------------------
  
  /**
   * Initialize the handler
   */
  async init(): Promise<HandlerCapabilities> {
    return {
      handlerId: this.handlerId,
      version: '1.0.0',
      protocolVersion: PROTOCOL_VERSION,
      location: this.location,
      capabilities: {
        discovery: false,
        transport: [],
        volume: false,
        queue: false,
        gapless: false,
        grouping: false,
        search: false,
        authentication: false,
        offline: false,
      },
      deviceTypes: [],
      requirements: {
        subscription: 'none',
        networkAccess: 'any',
      },
    };
  }
  
  /**
   * Handle incoming command (client doesn't handle commands, only sends them)
   */
  async handleCommand(_message: BatonMessage<CommandPayload>): Promise<BatonMessage<ResponsePayload> | null> {
    // Client doesn't handle commands, only sends them
    return null;
  }
  
  /**
   * Shutdown the handler
   */
  async shutdown(): Promise<void> {
    console.log('[ClientViewHandler] Shutting down...');
    this.eventListeners.clear();
    this.ws = null;
    console.log('[ClientViewHandler] Shutdown complete');
  }
  
  // ---------------------------------------------------------------------------
  // ACCESSORS
  // ---------------------------------------------------------------------------
  
  /**
   * Get this device's ID
   */
  getDeviceId(): string {
    return this.deviceId;
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let clientViewHandlerInstance: ClientViewHandler | null = null;

/**
 * Get the singleton ClientViewHandler instance
 */
export function getClientViewHandler(): ClientViewHandler {
  if (!clientViewHandlerInstance) {
    clientViewHandlerInstance = new ClientViewHandler();
  }
  return clientViewHandlerInstance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetClientViewHandler(): void {
  if (clientViewHandlerInstance) {
    clientViewHandlerInstance.shutdown();
    clientViewHandlerInstance = null;
  }
}

// Export default instance for convenience
export const viewHandler = getClientViewHandler();

